import React from 'react';

export type SmartFeedbackType =
  | 'typo'
  | 'synonym'
  | 'layout'
  | 'grammar'
  | 'wrong'
  | 'srs_wrong'
  | 'reset_to_mc';

export interface SmartInputFeedbackProps {
  userInput: string;
  expectedAnswer: string;
  otherTranslations?: string[];
  feedbackType: SmartFeedbackType;
  aiHint?: string | null;
  typoCorrection?: string | null;
  livesLeft?: number | null;
  maxLives?: number;
  isSrsReview?: boolean;
  style?: React.CSSProperties;
}

interface DiffPart {
  type: 'same' | 'del' | 'ins';
  char: string;
}

/**
 * Вычисляет LCS посимвольный diff между введённым словом и правильным.
 */
export function diffChars(str1: string, str2: string): { userParts: DiffPart[]; targetParts: DiffPart[] } {
  const m = str1.length;
  const n = str2.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1].toLowerCase() === str2[j - 1].toLowerCase()) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  let i = m;
  let j = n;
  const userParts: DiffPart[] = [];
  const targetParts: DiffPart[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && str1[i - 1].toLowerCase() === str2[j - 1].toLowerCase()) {
      userParts.unshift({ type: 'same', char: str1[i - 1] });
      targetParts.unshift({ type: 'same', char: str2[j - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      targetParts.unshift({ type: 'ins', char: str2[j - 1] });
      j--;
    } else if (i > 0 && (j === 0 || dp[i][j - 1] < dp[i - 1][j])) {
      userParts.unshift({ type: 'del', char: str1[i - 1] });
      i--;
    }
  }

  return { userParts, targetParts };
}

function simpleMarkdown(text: unknown): string {
  const safe = typeof text === 'string'
    ? text
    : text == null
      ? ''
      : (() => { try { return JSON.stringify(text); } catch { return String(text); } })();
  return safe
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--theme-text, #ffffff)">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:rgba(255,255,255,0.1);padding:1px 5px;border-radius:4px;font-size:12px">$1</code>')
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, ' ');
}

export function SmartInputFeedback({
  userInput,
  expectedAnswer,
  otherTranslations = [],
  feedbackType,
  aiHint,
  typoCorrection,
  livesLeft,
  maxLives = 3,
  isSrsReview = false,
  style,
}: SmartInputFeedbackProps) {
  // Настройки цветовой схемы и текстов
  const configs: Record<SmartFeedbackType, {
    title: string;
    bg: string;
    border: string;
    badgeText: string;
    badgeBg: string;
    badgeColor: string;
  }> = {
    typo: {
      title: '✏️ Опечатка',
      bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.04) 100%)',
      border: '1px solid rgba(245, 158, 11, 0.35)',
      badgeText: livesLeft != null ? '🛡️ Жизни в безопасности' : '✏️ Опечатка',
      badgeBg: 'rgba(245, 158, 11, 0.2)',
      badgeColor: '#f59e0b',
    },
    synonym: {
      title: '💡 Другой перевод',
      bg: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.04) 100%)',
      border: '1px solid rgba(16, 185, 129, 0.35)',
      badgeText: livesLeft != null ? '🛡️ Жизни в безопасности' : '💡 Другой вариант',
      badgeBg: 'rgba(16, 185, 129, 0.2)',
      badgeColor: '#10b981',
    },
    layout: {
      title: '⌨️ Раскладка клавиатуры',
      bg: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(37, 99, 235, 0.04) 100%)',
      border: '1px solid rgba(59, 130, 246, 0.35)',
      badgeText: livesLeft != null ? '🛡️ Жизни в безопасности' : '⌨️ Раскладка',
      badgeBg: 'rgba(59, 130, 246, 0.2)',
      badgeColor: '#60a5fa',
    },
    grammar: {
      title: '🔄 Форма слова',
      bg: 'linear-gradient(135deg, rgba(168, 85, 247, 0.12) 0%, rgba(147, 51, 234, 0.04) 100%)',
      border: '1px solid rgba(168, 85, 247, 0.35)',
      badgeText: livesLeft != null ? '🛡️ Жизни в безопасности' : '🔄 Форма слова',
      badgeBg: 'rgba(168, 85, 247, 0.2)',
      badgeColor: '#c084fc',
    },
    wrong: {
      title: '❌ Неверный ответ',
      bg: 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.04) 100%)',
      border: '1px solid rgba(239, 68, 68, 0.35)',
      badgeText: livesLeft != null ? `💔 -1 жизнь (${livesLeft} из ${maxLives})` : '❌ Попробуй ещё раз',
      badgeBg: 'rgba(239, 68, 68, 0.2)',
      badgeColor: '#f87171',
    },
    srs_wrong: {
      title: '📉 Повторим пораньше',
      bg: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.04) 100%)',
      border: '1px solid rgba(99, 102, 241, 0.35)',
      badgeText: '♾️ Без жизней • Интервал уменьшен',
      badgeBg: 'rgba(99, 102, 241, 0.2)',
      badgeColor: '#818cf8',
    },
    reset_to_mc: {
      title: '💔 Закончились жизни',
      bg: 'linear-gradient(135deg, rgba(225, 29, 72, 0.15) 0%, rgba(159, 18, 57, 0.06) 100%)',
      border: '1px solid rgba(225, 29, 72, 0.45)',
      badgeText: '🤍🤍🤍 Сброс в тест (MC)',
      badgeBg: 'rgba(225, 29, 72, 0.25)',
      badgeColor: '#fb7185',
    },
  };

  const conf = configs[feedbackType] || configs.wrong;

  // Дифф по символам для опечаток
  const targetForDiff = typoCorrection || expectedAnswer;
  const canDiff = feedbackType === 'typo' && userInput.trim().length >= 2 && targetForDiff.length >= 2;
  const diffResult = canDiff ? diffChars(userInput.trim(), targetForDiff.trim()) : null;

  // Очистка дополнительных переводов от целевого ответа и ввода пользователя
  const normStr = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е');
  const cleanOtherTranslations = otherTranslations.filter(t => {
    const nt = normStr(t);
    return nt && nt !== normStr(expectedAnswer) && nt !== normStr(userInput);
  });

  // Проверяем, не является ли aiHint дублирующим шаблонным текстом бэкенда
  const normExpected = normStr(expectedAnswer);
  const isBoilerplateHint = !aiHint ||
    feedbackType === 'typo' ||
    aiHint.includes('Почти правильно! Проверьте написание') ||
    normStr(aiHint).includes(`правильный ответ: ${normExpected}`) ||
    normStr(aiHint).includes(`правильный ответ: **${normExpected}**`);

  return (
    <div
      style={{
        background: conf.bg,
        border: conf.border,
        borderRadius: 16,
        padding: '12px 14px',
        marginBottom: 14,
        textAlign: 'left',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
        transition: 'all 0.3s ease',
        ...style,
      }}
    >
      {/* 1. Шапка: Название ситуации + Бейдж статуса жизней в одну строку */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--theme-text, #ffffff)',
            lineHeight: 1.2,
          }}
        >
          {conf.title}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            padding: '2px 8px',
            borderRadius: 999,
            background: conf.badgeBg,
            color: conf.badgeColor,
            whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}
        >
          {conf.badgeText}
        </span>
      </div>

      {/* 2. Блок сравнения ответов */}
      <div
        style={{
          background: 'rgba(0, 0, 0, 0.35)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: 12,
          padding: '10px 12px',
          marginBottom: (!isBoilerplateHint && aiHint) || feedbackType === 'reset_to_mc' ? 8 : 0,
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        {diffResult ? (
          /* Посимвольный diff с контрастным выделением ТОЛЬКО изменённых букв */
          <div>
            <div style={{ color: 'var(--theme-text-secondary, rgba(255, 255, 255, 0.6))', marginBottom: 4 }}>
              Вы ввели:{' '}
              <span style={{ fontWeight: 600, color: 'var(--theme-text, #ffffff)' }}>
                {diffResult.userParts.map((p, idx) => (
                  <span
                    key={idx}
                    style={p.type === 'del' ? {
                      background: 'rgba(244, 63, 94, 0.28)',
                      color: '#fb7185',
                      textDecoration: 'line-through',
                      borderRadius: 4,
                      padding: '1px 3px',
                      fontWeight: 800,
                    } : undefined}
                  >
                    {p.char}
                  </span>
                ))}
              </span>
            </div>
            <div style={{ color: 'var(--theme-text-secondary, rgba(255, 255, 255, 0.6))' }}>
              Надо:{' '}
              <span style={{ fontWeight: 600, color: 'var(--theme-text, #ffffff)' }}>
                {diffResult.targetParts.map((p, idx) => (
                  <span
                    key={idx}
                    style={p.type === 'ins' ? {
                      background: 'rgba(16, 185, 129, 0.28)',
                      color: '#34d399',
                      borderRadius: 4,
                      padding: '1px 3px',
                      fontWeight: 800,
                      textDecoration: 'underline',
                    } : undefined}
                  >
                    {p.char}
                  </span>
                ))}
              </span>
            </div>
          </div>
        ) : (
          /* Обычный режим (ввели vs правильный) */
          <div>
            <div style={{ color: 'var(--theme-text-secondary, rgba(255, 255, 255, 0.6))', marginBottom: 4 }}>
              Вы ввели:{' '}
              <span
                style={{
                  textDecoration: feedbackType === 'wrong' || feedbackType === 'reset_to_mc' ? 'line-through' : 'none',
                  color: feedbackType === 'synonym' ? '#f59e0b' : 'var(--theme-text, #ffffff)',
                  fontWeight: 600,
                }}
              >
                {userInput || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>(пусто)</span>}
              </span>
            </div>
            <div style={{ color: 'var(--theme-text-secondary, rgba(255, 255, 255, 0.6))' }}>
              Правильный ответ:{' '}
              <strong style={{ color: 'var(--theme-success, #10b981)', fontSize: 14 }}>
                {expectedAnswer}
              </strong>
            </div>
          </div>
        )}

        {/* Дополнительные переводы (без дублирования основного слова) */}
        {cleanOtherTranslations.length > 0 && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--theme-text-muted, rgba(255, 255, 255, 0.55))',
              marginTop: 6,
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              paddingTop: 5,
            }}
          >
            Также подходит: {cleanOtherTranslations.slice(0, 3).join(', ')}
          </div>
        )}
      </div>

      {/* 3. Пояснение от AI (только если несёт реальный смысл, а не дублирует ответ) */}
      {!isBoilerplateHint && aiHint && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--theme-text-secondary, rgba(255, 255, 255, 0.75))',
            lineHeight: 1.45,
          }}
          dangerouslySetInnerHTML={{ __html: simpleMarkdown(aiHint) }}
        />
      )}
      {feedbackType === 'reset_to_mc' && (
        <div style={{ fontSize: 12, color: 'var(--theme-text-secondary, rgba(255, 255, 255, 0.75))' }}>
          💡 Слово вернётся в тест (MC), чтобы закрепить его перед следующим вводом.
        </div>
      )}
    </div>
  );
}
